import type { ReactNode } from "react";
import { Box, useTheme } from "@mui/material";
import { AutoAwesomeRounded, SvgIconComponent } from "@mui/icons-material";
import { heroRise } from "./chatMotion";
import { ChatPreset } from "./presetPrompts";

interface PresetCardProps {
  icon: SvgIconComponent;
  label: ReactNode;
  /** Right-aligned figure, e.g. a live count. */
  trailing?: ReactNode;
  /** The static "what can this do" card is the entry point — always green. */
  accent?: boolean;
  disabled: boolean;
  delay: number;
  onClick: () => void;
}

export const PresetCard = ({
  icon: Icon,
  label,
  trailing,
  accent = false,
  disabled,
  delay,
  onClick,
}: PresetCardProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        // Icon INLINE, not stacked above the text. Stacking gave every card a
        // third row of height it did not need, and four tall cards under a
        // centred composer left the top of the page empty while the bottom
        // carried all the weight (Ben, 2026-07-31). Inline keeps a one-line
        // card to ~44px, so the block reads as a quiet footnote to the
        // composer rather than a second hero.
        display: "flex",
        // TOP, not centre. Grid rows stretch every card to the tallest in the
        // row, so a one-line label in a two-line row was being centred inside
        // that extra height and floated below its neighbour's first line
        // (Ben, 2026-07-31). Anchoring to the top gives every card in a row the
        // same icon-and-first-line baseline, whatever the label's length.
        alignItems: "flex-start",
        gap: 1.25,
        width: "100%",
        height: "100%",
        textAlign: "left",
        px: 1.5,
        py: 1.25,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        borderRadius: 1.5,
        fontFamily: "inherit",
        fontSize: "0.8rem",
        fontWeight: 500,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.07)"
          : "1px solid rgba(0, 0, 0, 0.07)",
        backgroundColor: "transparent",
        transition:
          "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
        "&:hover": {
          backgroundColor: isDark
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.03)",
          borderColor: "rgba(34, 197, 94, 0.4)",
          color: theme.palette.text.primary,
        },
        "&:hover .presetIcon": { color: "#22c55e" },
        // Touch has no hover, so the press itself has to acknowledge the tap.
        "&:active": { transform: disabled ? "none" : "scale(0.99)" },
        "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: 2 },
        ...heroRise(delay),
      }}
    >
      <Icon
        className="presetIcon"
        sx={{
          fontSize: 15,
          flex: "none",
          // Centres the 15px glyph against the first line's ~18px box
          // (0.8rem x 1.4), so the icon reads level with the text it labels
          // rather than with the top of the line.
          mt: "1.5px",
          color: accent ? "#22c55e" : theme.palette.text.disabled,
          transition: "color 0.2s ease",
        }}
      />
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Box>
      {trailing !== undefined && (
        <Box component="span" sx={{ flex: "none", fontVariantNumeric: "tabular-nums" }}>
          {trailing}
        </Box>
      )}
    </Box>
  );
};

interface ChatPresetsProps {
  presets: ChatPreset[];
  disabled: boolean;
  /** Sends the prompt as a first message — the composer's own path. */
  onAsk: (prompt: string) => void;
  /** Opens the capabilities sheet; never sends anything. */
  onExplain: () => void;
}

/**
 * Starter cards, two-up, sitting well below the composer.
 *
 * Full-width rows directly under the input were wrong twice over (Ben,
 * 2026-07-31). They matched the composer's exact width, so four stacked
 * elements of identical length read as one undifferentiated block with no
 * hierarchy — and stacking that much directly beneath the input dragged the
 * composer off the vertical centre the /research tab sits on.
 *
 * The grid fixes both: no single card mirrors the bar's length, while the
 * grid's outer edges still align to it, so the column keeps one shared
 * measure. Four cards land as a clean 2x2 — the static one plus three drawn.
 *
 * They are deliberately QUIET: transparent fill, hairline border, secondary
 * text, and no arrow. The composer is the primary action on this screen and
 * has to stay that way; these are the fallback for someone who doesn't know
 * what to type, so they earn contrast on hover rather than holding it at rest.
 *
 * ONE COLUMN on phones. Two 170px columns would wrap most of these questions
 * to four lines, and the objection that produced this grid doesn't apply
 * there anyway: on a phone the composer is full-width too, so a full-width
 * card is consistent rather than odd.
 *
 * The static card leads and never rotates, so there is always a way in for
 * someone who doesn't know what to ask. It is marked with the green icon and,
 * unlike its neighbours, doesn't start a chat — see ChatCapabilitiesSheet.
 */
export const ChatPresets = ({
  presets,
  disabled,
  onAsk,
  onExplain,
}: ChatPresetsProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ width: "100%" }}>
      {/* Names the block as examples rather than commands, and adds the third
          type size the composition was missing between the greeting and the
          card text. */}
      <Box
        sx={{
          fontSize: "0.62rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.09em",
          color: theme.palette.text.disabled,
          mb: 1,
          ...heroRise(0.14),
        }}
      >
        Get started
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
        }}
      >
        <PresetCard
          icon={AutoAwesomeRounded}
          label="How can I use Insight AI?"
          accent
          disabled={disabled}
          delay={0.18}
          onClick={onExplain}
        />
        {presets.map((preset, index) => (
          <PresetCard
            key={preset.prompt}
            icon={preset.icon}
            label={preset.prompt}
            disabled={disabled}
            delay={0.18 + (index + 1) * 0.05}
            onClick={() => onAsk(preset.prompt)}
          />
        ))}
      </Box>
    </Box>
  );
};
